// Getting a product in front of a reviewer.
//
// A product a vendor has been working on sits in Draft, and one that came
// back sits in Rejected with what the reviewer said. Both are waiting for the
// vendor to say "this is ready" — so they are gathered here, above the
// catalogue, with one button each and room to tell the reviewer what changed.
//
// What the product's state is stays the product's (`publishStatus`); what was
// said about it is the submission's.
import React, { useCallback, useEffect, useState } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import { showNotification } from "aldoo-ra/Notification"
import { ListSubmissions, ListVendorProducts, ReviewProduct } from "../AdminShared/api"
import { Chip, Empty, PublishChip, Section, Thumb, errorMessage } from "../AdminShared/ui"
import useSubmissionEvents from "./useSubmissionEvents"
import usePortalTexts from "./texts/usePortalTexts"
import { PRODUCTS } from "./texts/bundles"
import { EPublishStatus } from "../Store/extensions/shared/editor-config"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the states a vendor can do something about: a draft, a rejected one, one sent back with what to change
const WAITING_ON_THE_VENDOR = [EPublishStatus.Draft, EPublishStatus.Rejected, EPublishStatus.ReviewRequired]

// `onBlocked`: called instead of sending while a new contract waits for the company's signature
export default function SendForReview({ orgId, onBlocked }) {
  const t = usePortalTexts(PRODUCTS)
  const [items, setItems] = useState(null)
  // what a reviewer said about each product, so a rejection is not a mystery
  const [saidByPid, setSaidByPid] = useState({})
  const [sending, setSending] = useState(null)
  const [notes, setNotes] = useState("")
  const [busy, setBusy] = useState(false)
  // the reminder can be put away; it comes back when the set of waiting
  // products changes (a new draft, a rejection)
  const [dismissedFor, setDismissedFor] = useState(null)
  const dismissKey = `vendor-send-for-review-dismissed-${orgId}`
  useEffect(() => {
    try {
      setDismissedFor(localStorage.getItem(dismissKey))
    } catch {
      setDismissedFor(null)
    }
  }, [dismissKey])

  const load = useCallback(() => {
    if (!orgId) return
    ListVendorProducts({ orgId, itemsPerPage: 50 }).then((res) => {
      if (errorMessage(res)) return setItems([])
      setItems((res.data || []).filter((product) => WAITING_ON_THE_VENDOR.includes(Number(product.publishStatus ?? 0))))
    })
    ListSubmissions("product-reviews", undefined, orgId).then((res) => {
      if (errorMessage(res)) return
      const latest = {}
      // newest first, so the first mention of a product is the last word on it
      for (const item of res.items || []) if (!latest[item.pid]) latest[item.pid] = item
      setSaidByPid(latest)
    })
  }, [orgId])

  useEffect(() => {
    setItems(null)
    load()
  }, [load])

  // a decision that lands while the vendor is looking
  useSubmissionEvents({ orgId, onEvent: load })

  const send = async () => {
    setBusy(true)
    try {
      const res = await ReviewProduct(sending.pid, "submit", notes.trim())
      // a physical product goes for review with its weight (the delivery price is computed from it)
      const failed = res?.error === "weight_missing" ? t`Enter the product's weight (the Shipping tab) before sending it for review: the delivery price is computed from it.` : errorMessage(res)
      if (failed) return showNotification({ message: failed, className: "bg-admin_error text-white" })
      showNotification({ message: t`Sent for review`, className: "bg-admin_success text-white" })
      setSending(null)
      setNotes("")
      load()
    } finally {
      setBusy(false)
    }
  }

  if (items === null || items.length === 0) return null
  const waiting = items.map((product) => product.pid).sort().join(",")
  if (dismissedFor === waiting) return null
  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey, waiting)
    } catch {}
    setDismissedFor(waiting)
  }

  return (
    <Section
      title={<span className="inline-flex items-center gap-1.5">{t`Ready to publish?`}<HelpHint helpKey="vendor.products.review#send" /></span>}
      actions={
        <button 
          type="button" 
          onClick={dismiss} 
          aria-label={t`Close`} 
          className="p-1 rounded-full text-admin_text_secondary hover:bg-admin_neutral_1 hover:text-admin_text" data-testid="send-for-review-close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      }
      data-testid="send-for-review"
    >
      <Typography variant="admin_txt1_dim">
        {t`These are not in the shop yet. Send one for review and somebody will look at it.`}
      </Typography>
      <div className="flex flex-col divide-y divide-admin_bordered/60">
        {items.map((product) => (
          <div key={product.pid} className="py-2.5 flex items-center gap-3" data-testid="reviewable-product">
            <Thumb src={product.image} name={product.name || product.pid} className="w-[50px] h-[50px] rounded shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">
                {product.name || product.pid}
              </div>
              <div className="text-xs text-admin_text_secondary truncate">
                {product.pid}
              </div>
              {saidByPid[product.pid]?.status === "rejected" && (
                <div className="text-xs text-admin_error mt-0.5" data-testid="review-feedback">
                  {saidByPid[product.pid].notes || t`It was not published, and no reason was given.`}
                </div>
              )}
            </div>
            <PublishChip status={product.publishStatus} />
            <HelpHint helpKey="vendor.products.review#states" />
            <Button
              variant="admin_secondary"
              className="h-[30px] text-xs px-3 shrink-0"
              onClick={() => (onBlocked ? onBlocked() : setSending(product))}
              data-testid="send-product-for-review"
            >
              {t`Send for review`}
            </Button>
          </div>
        ))}
      </div>

      {sending && (
        <Modal
          isOpen
          onClose={() => setSending(null)}
          variant="admin_compact"
          title={t`Send for review`}
          styleOverrides={{ container: "bg-admin_paper border-admin_bordered w-full max-w-[520px] rounded-md border shadow-lg" }}
        >
          <div className="flex flex-col gap-3 p-4">
            <div className="text-sm">{sending.name || sending.pid}</div>
            <div className="flex flex-col gap-1">
              <Typography 
                component="label"
                variant="admin_label_sm_dim" 
                htmlFor="review-notes"
              >
                {t`Anything the reviewer should know (optional)`}
              </Typography>
              <Input
                variant="admin"
                multiline
                id="review-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={4}
                placeholder={t`What changed, what it is, who it is for`}
                className="text-sm"
                data-testid="review-notes"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="admin_secondary"
                onClick={() => setSending(null)}
              >
                {t`Cancel`}
              </Button>
              <Button
                variant="admin_primary"
                onClick={send}
                disabled={busy}
                data-testid="review-confirm"
              >
                {busy ? t`Sending…` : t`Send`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  )
}
